import { FC, ReactElement, useEffect, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { setCqlBebyggelseLayerWMS, setVisibleBackgroundLayers, setVisibleForegroundLayers } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaCheckbox, RaaDrawer } from 'raa-react';

import { DrawerBody, DrawerContainer, DrawerSection } from 'components/drawerSections/DrawerSections';
import { TextButton } from 'components/sharedElements/sharedElements';

import { BebyggelseklassEnum, Layer, SkyddstypEnum } from 'enums';

import './LayerDrawer.scss';

import classNames from 'classnames';

interface SelectableBackgroundLayer {
  layer: Layer;
  image: string;
  text: string;
}

interface LayerChildNode {
  label: string;
  checked: boolean;
  cql?: Array<string>;
}

interface LayerNode {
  label: string;
  symbolLayer: Layer;
  geometryLayer: Layer;
  checked: boolean;
  expanded: boolean;
  cql?: string;
  children: Array<LayerChildNode>;
}

const isChecked = (node: LayerNode | LayerChildNode) => node.checked;

const isNotChecked = (node: LayerNode | LayerChildNode) => !node.checked;

const isIndeterminate = (node: LayerNode) => node.children.some(isChecked) && node.children.some(isNotChecked);

const isCheckedOrIndeterminate = (node: LayerNode) => isChecked(node) || isIndeterminate(node);

const setChildNodesChecked = (node: LayerNode, checked: boolean): LayerNode => ({
  ...node,
  children: node.children.map((c) => ({ ...c, checked }))
});

const layerNodeShouldBeDisabled = (node: LayerNode, nodes: LayerNode[]): boolean =>
  nodes.filter(isCheckedOrIndeterminate).length === 1 && isChecked(node);

const layerNodeChildShouldBeDisabled = (node: LayerNode, nodes: LayerNode[], child: LayerChildNode): boolean =>
  nodes.filter(isCheckedOrIndeterminate).length === 1 &&
  node.children.filter(isChecked).length === 1 &&
  isChecked(child);

const defaultLayerNodes: Array<LayerNode> = [
  {
    label: 'Statliga byggnadsminnen',
    symbolLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS,
    geometryLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS,
    checked: true,
    expanded: false,
    cql: SkyddstypEnum.STATLIGT_BYGGNADSMINNE,
    children: [
      {
        label: 'Skyddsområden',
        checked: true,
        cql: [BebyggelseklassEnum.SAMMANSATT]
      },
      {
        label: 'Bebyggelseobjekt',
        checked: true,
        cql: [BebyggelseklassEnum.ANLAGGNING, BebyggelseklassEnum.BYGGNAD, BebyggelseklassEnum.PLATS]
      }
    ]
  },
  {
    label: 'Byggnadsminnen',
    symbolLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS,
    geometryLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS,
    checked: true,
    expanded: false,
    cql: SkyddstypEnum.BYGGNADSMINNE,
    children: [
      {
        label: 'Skyddsområden',
        checked: true,
        cql: [BebyggelseklassEnum.SAMMANSATT]
      },
      {
        label: 'Bebyggelseobjekt',
        checked: true,
        cql: [BebyggelseklassEnum.ANLAGGNING, BebyggelseklassEnum.BYGGNAD, BebyggelseklassEnum.PLATS]
      }
    ]
  },
  {
    label: 'Kyrkliga kulturminnen',
    checked: true,
    expanded: false,
    symbolLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS,
    geometryLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS,
    cql: SkyddstypEnum.KYRKLIGT_KULTURMINNE,
    children: []
  },
  {
    label: 'Anmälningsplikt enligt KML',
    checked: true,
    expanded: false,
    symbolLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS,
    geometryLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS,
    cql: SkyddstypEnum.ANMALNINGSPLIKT,
    children: [
      {
        label: 'Skyddsområden',
        checked: true,
        cql: [BebyggelseklassEnum.SAMMANSATT]
      },
      {
        label: 'Bebyggelseobjekt',
        checked: true,
        cql: [BebyggelseklassEnum.ANLAGGNING, BebyggelseklassEnum.BYGGNAD, BebyggelseklassEnum.PLATS]
      }
    ]
  },
  {
    label: 'Identifierade kulturvärden',
    checked: true,
    expanded: false,
    symbolLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS,
    geometryLayer: Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS,
    cql: SkyddstypEnum.IDENTIFIERAT_KULTURVARDE,
    children: [
      {
        label: 'Bebyggelseobjekt',
        checked: true,
        cql: [BebyggelseklassEnum.ANLAGGNING, BebyggelseklassEnum.BYGGNAD, BebyggelseklassEnum.PLATS]
      },
      {
        label: 'Bebyggelseområden',
        checked: true,
        cql: [BebyggelseklassEnum.OMRADE]
      }
    ]
  },
  {
    label: 'Inventeringar',
    checked: false,
    expanded: false,
    symbolLayer: Layer.PUBLICERADE_AKTIVITETER_SYMBOLER_WMTS,
    geometryLayer: Layer.PUBLICERADE_AKTIVITETER_YTOR_WMTS,
    cql: '',
    children: []
  }
];

const backgroundLayers: Array<SelectableBackgroundLayer> = [
  {
    image: 'sprite-sverigekarta',
    layer: Layer.TOPOWEB,
    text: 'Karta'
  },
  {
    image: 'sprite-ortofoto',
    layer: Layer.ORTOFOTO,
    text: 'Foto'
  },
  {
    image: 'sprite-skuggning',
    layer: Layer.SKUGGNING,
    text: 'Skuggning'
  },
  {
    image: 'sprite-lutning',
    layer: Layer.LUTNING,
    text: 'Lutning'
  },
  {
    image: 'sprite-sjokort',
    layer: Layer.SJOKORT,
    text: 'Sjökort'
  }
];

const LayerDrawer: FC = (): ReactElement => {
  const visibleBackgroundLayers = useAppSelector((state: RootState) => state.map.visibleBackgroundLayers);
  const visibleForegroundLayers = useAppSelector((state: RootState) => state.map.visibleForegroundLayers);

  const dispatch = useAppDispatch();

  const [layerDrawerOpen, setLayerDrawerOpen] = useState<boolean>(false);
  const [layerNodes, setLayerNodes] = useState<Array<LayerNode>>(defaultLayerNodes);
  const [symbolerLayerVisible, setSymbolerLayerVisible] = useState<boolean>(true);
  const [ytaLayerVisible, setYtaLayerVisible] = useState<boolean>(false);

  const toggleBackgroundLayer = (layer: Layer) => {
    if (visibleBackgroundLayers.includes(layer)) {
      dispatch(setVisibleBackgroundLayers(visibleBackgroundLayers.filter((l) => layer !== l)));
    } else {
      dispatch(setVisibleBackgroundLayers([...visibleBackgroundLayers, layer]));
    }
  };

  const toggleForegroundLayer = (layer: Layer) => {
    if (visibleForegroundLayers.includes(layer)) {
      dispatch(setVisibleForegroundLayers(visibleForegroundLayers.filter((l) => layer !== l)));
    } else {
      dispatch(setVisibleForegroundLayers([...visibleForegroundLayers, layer]));
    }
  };

  const handleLayerNodeChange = (previousNode: LayerNode, change: (l: LayerNode) => LayerNode) => {
    const newNodes = layerNodes.map((node) =>
      node === previousNode ? setChildNodesChecked(change(node), change(node).checked) : node
    );
    setLayerNodes([...newNodes]);
  };

  const handleLayerChildNodeChange = (
    previousChildNode: LayerChildNode,
    change: (l: LayerChildNode) => LayerChildNode
  ) => {
    const newNodes = layerNodes
      .map((node) => ({
        ...node,
        children: node.children.map((child) => (child === previousChildNode ? change(child) : child))
      }))
      .map((node) => ({ ...node, checked: node.children.length > 0 ? node.children.every(isChecked) : node.checked }));
    setLayerNodes([...newNodes]);
  };

  useEffect(() => {
    const nonApplicableLayers = visibleForegroundLayers.filter(
      (layer) => !layerNodes.some((layerNode) => layerNode.symbolLayer === layer || layerNode.geometryLayer === layer)
    );

    const addLayerIfVisible = (node: LayerNode): Layer[] => {
      const result = [];
      ytaLayerVisible && result.push(node.geometryLayer);
      symbolerLayerVisible && result.push(node.symbolLayer);
      return result;
    };

    const checkedLayers = Array.from(
      new Set(layerNodes.filter(isCheckedOrIndeterminate).map(addLayerIfVisible).flat())
    );

    const newLayers = [...nonApplicableLayers, ...checkedLayers];

    const sameContent = (a: Layer[], b: Layer[]) => a.every((l) => b.includes(l) && b.every((l) => a.includes(l)));

    if (!sameContent(visibleForegroundLayers, newLayers)) {
      dispatch(setVisibleForegroundLayers([...newLayers]));
    }
  }, [ytaLayerVisible, symbolerLayerVisible, layerNodes, visibleForegroundLayers, dispatch]);

  useEffect(() => {
    const cql = layerNodes
      .filter(
        (node) =>
          node.symbolLayer === Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS &&
          node.geometryLayer === Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS
      )
      .filter((node) => node.checked || isIndeterminate(node))
      .map(
        (node) =>
          `(skyddstyp = '${node.cql}' ${
            node.children.length > 0
              ? `AND bebyggelseklass IN (${node.children
                  .filter(isChecked)
                  .map((child) => child.cql)
                  .flat()
                  .map((klass) => `'${klass}'`)
                  .join(', ')})`
              : ''
          })`
      )
      .join(' OR ');

    dispatch(setCqlBebyggelseLayerWMS(cql));
  }, [layerNodes, dispatch]);

  return (
    <>
      <RaaDrawer
        zIndex="49"
        drawerOpen={layerDrawerOpen ? 'open' : 'closed'}
        position="right"
        drawerSize="small"
        handleIsVisible={false}
        drawerHandleClicked={() => 'closed'}
      >
        <DrawerContainer>
          <div className="tw-flex tw-justify-end tw-p-2">
            <TextButton icon="raa-icon-close" reverse={true} uppercase={true} onClick={() => setLayerDrawerOpen(false)}>
              stäng
            </TextButton>
          </div>

          <DrawerBody>
            <div className="tw-overflow-y-overlay tw-ml-4 tw-flex">
              {backgroundLayers.map((backgroundLayer: SelectableBackgroundLayer) => (
                <button
                  key={backgroundLayer.text}
                  className={classNames(
                    'raa-button-focus tw-mx-1 tw-mb-4 tw-mt-4 tw-flex tw-w-[72px] tw-flex-col tw-items-center tw-justify-center tw-rounded-[4px] tw-px-1 tw-py-2 tw-text-raa-orange hover:tw-bg-raa-gray-2',
                    {
                      'tw-bg-raa-gray-5 tw-font-semibold tw-text-raa-white hover:tw-bg-raa-gray-5':
                        visibleBackgroundLayers.includes(backgroundLayer.layer)
                    }
                  )}
                  disabled={visibleBackgroundLayers.includes(backgroundLayer.layer)}
                  onClick={() => {
                    const remainder = visibleBackgroundLayers.filter(
                      (layer) =>
                        !backgroundLayers.some(
                          (selectableBackgroundLayer: SelectableBackgroundLayer) =>
                            selectableBackgroundLayer.layer === layer
                        )
                    );
                    dispatch(setVisibleBackgroundLayers([...remainder, backgroundLayer.layer]));
                  }}
                >
                  <span className={`sprite-karta-legends tw-block tw-rounded-[4px] ${backgroundLayer.image}`} />
                  <span className="tw-mt-2 tw-text-size-0.813 tw-uppercase">{backgroundLayer.text}</span>
                </button>
              ))}
            </div>
            <DrawerSection className="tw-p-4">
              <h3 className="tw-heading-md tw-pb-3">Informationslager</h3>
              <ul>
                {layerNodes.map((layerNode: LayerNode) => (
                  <li key={layerNode.label}>
                    <div className="tw-flex tw-h-[40px] tw-justify-between">
                      <RaaCheckbox
                        labelContent={layerNode.label}
                        checked={layerNode.checked}
                        disabled={layerNodeShouldBeDisabled(layerNode, layerNodes)}
                        indeterminate={isIndeterminate(layerNode)}
                        onChange={() => handleLayerNodeChange(layerNode, (l) => ({ ...l, checked: !l.checked }))}
                      />

                      {layerNode.children.length > 0 && (
                        <button
                          className="raa-button-focus tw-mr-1 tw-h-10 tw-w-10 tw-rounded"
                          aria-label={'expandera ' + layerNode.label}
                          onClick={() => handleLayerNodeChange(layerNode, (l) => ({ ...l, expanded: !l.expanded }))}
                        >
                          <span
                            aria-hidden="true"
                            aria-label={`Visa ${layerNode.label}`}
                            className={classNames(
                              'raa-icon-arrow-down tw-block tw-transform tw-text-size-1.75 tw-text-raa-orange tw-transition tw-duration-500 tw-ease-in-out',
                              { 'tw-rotate-180': layerNode.expanded }
                            )}
                          />
                        </button>
                      )}
                    </div>
                    {layerNode.children.length > 0 && (
                      <TransitionGroup component="ul">
                        {layerNode.children
                          .filter(() => layerNode.expanded)
                          .map((child, index) => (
                            <CSSTransition timeout={150} classNames="fade" key={child.label + index}>
                              <li className="tw-ml-6 tw-flex tw-h-[40px]">
                                <RaaCheckbox
                                  labelContent={child.label}
                                  checked={child.checked}
                                  disabled={layerNodeChildShouldBeDisabled(layerNode, layerNodes, child)}
                                  onChange={() =>
                                    handleLayerChildNodeChange(child, (c) => ({ ...c, checked: !c.checked }))
                                  }
                                />
                              </li>
                            </CSSTransition>
                          ))}
                      </TransitionGroup>
                    )}
                  </li>
                ))}
              </ul>
              <span className="raa-secondary-text">*Minst ett lager måste vara aktivt</span>
            </DrawerSection>
            <DrawerSection className="tw-flex tw-flex-col tw-p-4">
              <h3 className="tw-heading-md tw-pb-3">Visning</h3>
              <RaaCheckbox
                labelContent="Ikoner"
                disabled={!ytaLayerVisible}
                checked={symbolerLayerVisible}
                onChange={() => setSymbolerLayerVisible(!symbolerLayerVisible)}
              />
              <RaaCheckbox
                labelContent="Geometrier"
                disabled={!symbolerLayerVisible}
                checked={ytaLayerVisible}
                onChange={() => setYtaLayerVisible(!ytaLayerVisible)}
              />
            </DrawerSection>
            <DrawerSection className="tw-flex tw-flex-col tw-p-4">
              <h3 className="tw-heading-md tw-pb-3">Gränser</h3>
              <RaaCheckbox
                labelContent="Fastigheter"
                checked={visibleBackgroundLayers.includes(Layer.FASTIGHETSGRANSER_WMS)}
                onChange={() => toggleBackgroundLayer(Layer.FASTIGHETSGRANSER_WMS)}
              />
              <RaaCheckbox
                labelContent="Socknar"
                checked={visibleForegroundLayers.includes(Layer.SOCKEN_WMS)}
                onChange={() => toggleForegroundLayer(Layer.SOCKEN_WMS)}
              />
              <RaaCheckbox
                labelContent="Landskap"
                checked={visibleForegroundLayers.includes(Layer.LANDSKAP_WMS)}
                onChange={() => toggleForegroundLayer(Layer.LANDSKAP_WMS)}
              />
            </DrawerSection>
          </DrawerBody>
        </DrawerContainer>
      </RaaDrawer>
      <div className="tw-absolute tw-right-0 tw-top-0 tw-z-10 tw-p-4">
        <button
          onClick={() => setLayerDrawerOpen(true)}
          className="raa-icon-layers tw-text-size-1.313; tw-pointer-events-auto tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded tw-bg-raa-white tw-leading-1.875 tw-shadow-md hover:tw-bg-raa-gray-5 hover:tw-text-raa-white"
          type="button"
          title="Informationslager"
          aria-label="informationslager"
        />
      </div>
    </>
  );
};

export default LayerDrawer;
